/*
 * AppDetailsLicensing.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";
import { Switch, TextField, Grid2, Container, Typography } from "@mui/material";
import "./AppDetailsLicensing.css";
import { getConfig } from "../../config";
import { fetchLicensingActionsProvided } from "../../Services/redux/LicensingsActions/LicensingActions";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 32,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(15.5px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#1890ff",
        ...theme.applyStyles("dark", {
          backgroundColor: "#177ddc",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

function AppDetailsLicensing({
  postCallTheNewSubmission,
  checkAllTabsAreTrue,
}) {
  const dispatch = useDispatch();
  const [serviceIdEnable, setServiceIdEnable] = useState(false);
  const [productIdEnable, setProductIdEnable] = useState(false);
  const [serviceId, setServiceId] = useState([]);
  const [productId, setProductId] = useState([]);
  const [errorServiceId, setErrorServiceId] = useState(false);
  const [errorProductId, setErrorProductId] = useState(false);
  const { submissionDetails } = useSelector((state) => state.submissionDetails);
  const [isEditingServiceId, setIsEditingServiceId] = useState(false);
  const [isEditingProductId, setIsEditingProductId] = useState(false);

  // Helper function to safely call checkAllTabsAreTrue
  const safeCheckAllTabsAreTrue = useCallback(
    (value) => {
      if (typeof checkAllTabsAreTrue === "function") {
        checkAllTabsAreTrue(value);
      }
    },
    [checkAllTabsAreTrue],
  );

  // Helper function to safely call postCallTheNewSubmission
  const safePostCallTheNewSubmission = useCallback(
    (data) => {
      if (typeof postCallTheNewSubmission === "function") {
        postCallTheNewSubmission(data);
      }
    },
    [postCallTheNewSubmission],
  );

  const handleSellerPortalInit = () => {
    if (!submissionDetails || Object.keys(submissionDetails).length === 0)
      return;

    const noIdsEnabled =
      !submissionDetails.enableServiceIds &&
      !submissionDetails.enableProductIds;
    if (noIdsEnabled) {
      dispatch(fetchLicensingActionsProvided(false));
      setServiceIdEnable(false);
      setProductIdEnable(false);
      return;
    }

    const serviceIdsEmpty =
      submissionDetails.enableServiceIds &&
      submissionDetails.associatedServiceIds &&
      (submissionDetails.associatedServiceIds.length === 0 ||
        submissionDetails.associatedServiceIds[0] === "");

    const productIdsEmpty =
      submissionDetails.enableProductIds &&
      submissionDetails.associatedProductIds &&
      (submissionDetails.associatedProductIds.length === 0 ||
        submissionDetails.associatedProductIds[0] === "");

    dispatch(fetchLicensingActionsProvided(serviceIdsEmpty || productIdsEmpty));
    setServiceIdEnable(submissionDetails.enableServiceIds);
    setProductIdEnable(submissionDetails.enableProductIds);

    if (!isEditingServiceId) {
      setServiceId([...(submissionDetails.associatedServiceIds || [])]);
    }
    if (!isEditingProductId) {
      setProductId([...(submissionDetails.associatedProductIds || [])]);
    }
  };

  const handleOtherPortalInit = () => {
    if (!submissionDetails || Object.keys(submissionDetails).length === 0)
      return;

    if (
      submissionDetails.associatedServiceIds &&
      submissionDetails.associatedServiceIds.length > 0
    ) {
      if (!isEditingServiceId) {
        setServiceId([...submissionDetails.associatedServiceIds]);
      }
      setServiceIdEnable(true);
    }

    if (
      submissionDetails.associatedProductIds &&
      submissionDetails.associatedProductIds.length > 0
    ) {
      if (!isEditingProductId) {
        setProductId([...submissionDetails.associatedProductIds]);
      }
      setProductIdEnable(true);
    }
  };

  useEffect(() => {
    const isSellerOrDomainApprover =
      getConfig().REACT_APP_PORTAL === "seller" ||
      getConfig().REACT_APP_PORTAL === "domain-approver";

    if (isSellerOrDomainApprover) {
      handleSellerPortalInit();
    } else {
      handleOtherPortalInit();
    }
  }, [
    submissionDetails,
    isEditingServiceId,
    isEditingProductId,
    safeCheckAllTabsAreTrue,
  ]);

  const enableLicensingData = ($event, index) => {
    let newSubmissionData = { ...submissionDetails };
    const isChecked = $event.target.checked;

    if (index === 1) {
      setServiceIdEnable(isChecked);
      if (isChecked) {
        const hasErrors =
          productId.filter(Boolean).length === 0 ||
          serviceId.filter(Boolean).length === 0;
        setErrorServiceId(hasErrors);
        dispatch(fetchLicensingActionsProvided(hasErrors));
      } else {
        setErrorServiceId(false);
        dispatch(fetchLicensingActionsProvided(false));
      }
      newSubmissionData.enableServiceIds = isChecked;
    } else if (index === 2) {
      setProductIdEnable(isChecked);
      if (isChecked) {
        const hasErrors = productId.length === 0 || serviceId.length === 0;
        setErrorProductId(hasErrors);
        dispatch(fetchLicensingActionsProvided(hasErrors));
      } else {
        setErrorProductId(false);
        dispatch(fetchLicensingActionsProvided(false));
      }
      newSubmissionData.enableProductIds = isChecked;
    }
    delete newSubmissionData["legalDocuments"];

    safePostCallTheNewSubmission(newSubmissionData);
  };

  const checkServiceIdValidation = (
    newValue,
    productId,
    productIdEnable,
    dispatch,
    safeCheckAllTabsAreTrue,
  ) => {
    const hasValidServiceId = newValue.some(Boolean);
    if (!hasValidServiceId) {
      safeCheckAllTabsAreTrue(false);
      dispatch(fetchLicensingActionsProvided(true));
      return;
    }

    if (productIdEnable && productId.filter(Boolean).length === 0) {
      safeCheckAllTabsAreTrue(false);
      dispatch(fetchLicensingActionsProvided(true));
      return;
    }

    safeCheckAllTabsAreTrue(true);
    dispatch(fetchLicensingActionsProvided(false));
  };

  const checkProductIdValidation = (
    newValue,
    serviceId,
    serviceIdEnable,
    dispatch,
    safeCheckAllTabsAreTrue,
  ) => {
    const hasValidProductId = newValue.some(Boolean);
    if (!hasValidProductId) {
      safeCheckAllTabsAreTrue(false);
      dispatch(fetchLicensingActionsProvided(true));
      return;
    }

    if (serviceIdEnable && serviceId.filter(Boolean).length === 0) {
      safeCheckAllTabsAreTrue(false);
      dispatch(fetchLicensingActionsProvided(true));
      return;
    }

    safeCheckAllTabsAreTrue(true);
    dispatch(fetchLicensingActionsProvided(false));
  };

  const parseIdsForUi = (value) => value.split(",").map((id) => id.trim());

  const parseIdsForPayload = (value) =>
    value
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

  const handleChange = ($event, index) => {
    const rawValue = $event.target.value;
    const isEmpty = rawValue.trim() === "";
    const parsedIds = parseIdsForUi(rawValue);
    if (index === 1) {  
      setIsEditingServiceId(true);
      setServiceId(parsedIds);
      setErrorServiceId(isEmpty);
      if (isEmpty) {
        console.log("Service ID input is empty, setting error state.");
        setErrorServiceId(true);
        safeCheckAllTabsAreTrue(false);
        setServiceId(rawValue === "" ? [] : parseIdsForUi(rawValue));
        dispatch(fetchLicensingActionsProvided(true));
      } else {
        console.log("Service ID input changed to222:", $event.target.value);
        setErrorServiceId(false);
        const newServiceIds = parseIdsForUi($event.target.value);
        checkServiceIdValidation(
          newServiceIds,
          productId,
          productIdEnable,
          dispatch,
          safeCheckAllTabsAreTrue,
        );
      }
    } else if (index === 2) {
      setIsEditingProductId(true);
      setProductId(parsedIds);
       setErrorProductId(isEmpty);
      if (isEmpty) {
        safeCheckAllTabsAreTrue(false);
        dispatch(fetchLicensingActionsProvided(true));
      } else {
        setErrorProductId(false);
        const newProductIds = parseIdsForUi($event.target.value);
        checkProductIdValidation(
          newProductIds,
          serviceId,
          serviceIdEnable,
          dispatch,
          safeCheckAllTabsAreTrue,
        );
      }
    }
  };

  const sendSubmission = (index, rawValue) => {
    let newSubmissionData = { ...submissionDetails };
    const parsedIds = parseIdsForPayload(rawValue ?? "");

    if (index === 1) {
      newSubmissionData.associatedServiceIds = parsedIds;
      setServiceId(parsedIds);
    } else if (index === 2) {
      newSubmissionData.associatedProductIds = parsedIds;
      setProductId(parsedIds);
    }

    if (!errorServiceId && !errorProductId) {
      delete newSubmissionData["legalDocuments"];
      safePostCallTheNewSubmission(newSubmissionData);
    }

    if (index === 1) setIsEditingServiceId(false);
    if (index === 2) setIsEditingProductId(false);
  };

  return (
    <Container maxWidth="lg">
      <Grid2 container justifyContent="flex-start" alignItems="center">
        <Grid2 size={12}>
          <section
            className="Licensing-section"
            id="licensing"
            style={
              submissionDetails &&
              Object.keys(submissionDetails).length > 0 &&
              submissionDetails.status !== "DRAFT"
                ? { marginTop: "30px" }
                : { marginTop: "15px", width: "85%" }
            }
          >
            <Typography
              sx={{
                "font-weight": "bold !important",
                "font-size": "20px !important",
                // "letter-spacing": "0.4px !important",
                color: "rgba(18, 20, 21, 1) !important",
              }}
              className="uploadSignedApkText"
            >
              Licensing
            </Typography>
            <p className="licensing-para" style={{ marginTop: "12px" }}>
              It is possible to limit the availability of your app based on the
              existing ODP Licensing models for ODP 1.0 or ODP 1.5 vehicles.
              When you provide us such information, your app will be made
              available if atleast one of the named service-ids or product-ids
              is available and active in the vehicles.
            </p>
            {submissionDetails && Object.keys(submissionDetails).length > 0 && (
              <div className="licensing-section-div">
                <div className="licensing-service-id">
                  <div className="service-id-box">
                    <p className="service-id-text">
                      Is your app controlled by a service-ID in MOD3 (ODP 1.0)
                      based vehicles?
                    </p>
                    <AntSwitch
                      sx={{
                        marginRight: "15px !important",
                      }}
                      checked={serviceIdEnable}
                      onChange={($event) => {
                        enableLicensingData($event, 1);
                      }}
                      disabled={submissionDetails.status !== "DRAFT"}
                    />
                  </div>
                  <p className="below-text-licensing">
                    For MOD3 vehicles (ODP 1.0 based vehicles) group apps are
                    typically controlled via service-ids which can be configured
                    in MBConnect?
                  </p>
                  {serviceIdEnable && (
                    <div className="service-id-textbox">
                      <TextField
                        sx={{
                          "&.MuiFormControl-root": {
                            width: "90%",
                            padding: "4px 8px 4px 0px",
                            marginTop: "-12px",
                          },
                        }}
                        label={
                          serviceId.filter(Boolean).length === 0 && "serviceId"
                        }
                        slotProps={{
                          inputLabel: {
                            shrink: false,
                            style: {
                              "font-size": "14px",
                              "font-weight": 400,
                              "line-height": "16.41px",
                              "text-align": "left",
                              color: "rgba(113, 114, 115, 1)",
                            },
                          },
                        }}
                        value={serviceId.join(",")}
                        onChange={($event) => handleChange($event, 1)}
                        onBlur={(e) => sendSubmission(1, e.target.value)}
                        disabled={submissionDetails.status !== "DRAFT"}
                        error={errorServiceId}
                        helperText={
                          errorServiceId ? "Service ID is required." : ""
                        }
                      />
                    </div>
                  )}
                </div>
                <div className="licensing-service-id">
                  <div className="service-id-box">
                    <p className="service-id-text">
                      Is your app controlled by a product-ID in MOD4 (ODP 1.5)
                      based vehicles?
                    </p>
                    <AntSwitch
                      sx={{
                        marginRight: "15px !important",
                      }}
                      checked={productIdEnable}
                      onChange={($event) => {
                        enableLicensingData($event, 2);
                      }}
                      disabled={submissionDetails.status !== "DRAFT"}
                    />
                  </div>
                  <p className="below-text-licensing">
                    For MOD4 vehicles (ODP 1.5 based vehicles) group apps are
                    typically controlled via product-ids which can be configured
                    in the SCM Matrix and PMS.
                  </p>
                  {productIdEnable && (
                    <div className="service-id-textbox">
                      <TextField
                        sx={{
                          "&.MuiFormControl-root": {
                            width: "90%",
                            padding: "4px 8px 4px 0px",
                            marginTop: "-12px",
                          },
                        }}
                        label={
                          productId.filter(Boolean).length === 0 && "productId"
                        }
                        slotProps={{
                          inputLabel: {
                            shrink: false,
                            style: {
                              "font-size": "14px",
                              "font-weight": 400,
                              "line-height": "16.41px",
                              "text-align": "left",
                              color: "rgba(113, 114, 115, 1)",
                            },
                          },
                        }}
                        value={productId.join(",")}
                        onChange={($event) => handleChange($event, 2)}
                        onBlur={(e) => sendSubmission(2, e.target.value)}
                        disabled={submissionDetails.status !== "DRAFT"}
                        error={errorProductId}
                        helperText={
                          errorProductId ? "Product ID is required." : ""
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </Grid2>
      </Grid2>
    </Container>
  );
}

AppDetailsLicensing.propTypes = {
  postCallTheNewSubmission: PropTypes.func,
  checkAllTabsAreTrue: PropTypes.func,
};

AppDetailsLicensing.defaultProps = {
  postCallTheNewSubmission: () => {
    console.warn(
      "postCallTheNewSubmission function not provided to AppDetailsLicensing component",
    );
  },
  checkAllTabsAreTrue: () => {
    console.warn(
      "checkAllTabsAreTrue function not provided to AppDetailsLicensing component",
    );
  },
};

export default AppDetailsLicensing;
